import React, {useEffect, useState} from 'react';
import TuningPanel from './TuningPanel';
import {FAMILY_LABELS, getRecommendSettings, resetRecommendSettings, saveRecommendSettings} from '../../libraries/AdminApi';

const fmtWeight = (v) => Math.round(v * 100) + '%';

export default function SettingsPage() {
    const [info, setInfo] = useState(null);
    const [profile, setProfile] = useState('default');
    const [values, setValues] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const savedFor = (r, p) => (p === 'default' ? r.default : r.profiles[p].values);
    const apply = (r, p = profile) => { setInfo(r); setValues(savedFor(r, p)); };
    useEffect(() => { getRecommendSettings().then((r) => apply(r, 'default')).catch((e) => setError(e.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const pick = (p) => { setProfile(p); setValues(savedFor(info, p)); };
    const run = (promise) => {
        setBusy(true);
        promise.then((r) => apply(r)).catch((e) => setError(e.message)).finally(() => setBusy(false));
    };
    const isCustom = (p) => info && Object.keys(info.profiles[p]?.overrides || {}).length > 0;
    const label = profile === 'default' ? 'Default' : info?.profiles[profile]?.label || FAMILY_LABELS[profile] || profile;

    return (
        <div className="max-w-[980px]">
            <h1 className="font-heading text-[28px] font-bold tracking-[-0.015em]">Settings</h1>
            <p className="mb-6 mt-1 text-[13px] text-white/45">
                How the engine scores recommendations, for the app and the API. The original song's genre family picks the profile;
                families without their own profile use Default. Try changes live in the Song Lab first.
            </p>
            {error && <p className="mb-4 text-[13px] text-apple-red">{error}</p>}

            {info && (
                <div className="grid gap-6 md:grid-cols-[260px_320px_1fr]">
                    <div className="rounded-2xl bg-white/[0.04] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                        {['default', ...Object.keys(info.profiles)].map((p) => {
                            const v = savedFor(info, p);
                            return (
                                <button key={p} type="button" onClick={() => pick(p)}
                                        className={'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[12.5px] transition '
                                            + (profile === p ? 'bg-white/[0.10] text-white' : 'text-white/60 hover:bg-white/[0.05] hover:text-white')}>
                                    <span className="flex-1 font-semibold">{p === 'default' ? 'Default' : info.profiles[p].label || FAMILY_LABELS[p] || p}</span>
                                    <span className="text-[10.5px] tabular-nums text-white/40">
                                        {fmtWeight(v.genre_exact)} · {fmtWeight(v.era)}
                                    </span>
                                    {p !== 'default' && (isCustom(p)
                                        ? <span className="rounded-full bg-apple-red/15 px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-apple-red">Custom</span>
                                        : <span className="w-[46px]"/>)}
                                </button>
                            );
                        })}
                        <p className="px-3 pb-1 pt-3 text-[10.5px] text-white/35">Numbers: same-genre bonus · era bonus.</p>
                    </div>

                    <TuningPanel values={values} saved={savedFor(info, profile)} onChange={setValues} busy={busy}
                                 saveLabel={profile === 'default' ? 'Save as default' : `Save ${label} profile`}
                                 onSave={() => run(saveRecommendSettings(profile, values))}
                                 onRevert={() => setValues(savedFor(info, profile))}
                                 header={<p className="mt-2 text-[11px] text-white/45">Editing: <b className="text-white/80">{label}</b>
                                     {profile !== 'default' && !isCustom(profile) && ' (uses Default until saved)'}</p>}
                                 footer={profile !== 'default' && isCustom(profile) && (
                                     <button type="button" disabled={busy}
                                             onClick={() => window.confirm(`Remove the ${label} profile?`) && run(resetRecommendSettings(profile))}
                                             className="mt-2 w-full rounded-xl py-1.5 text-[11.5px] text-white/45 hover:text-apple-red">
                                         Remove {label} profile
                                     </button>
                                 )}/>

                    <div className="space-y-4 text-[13px] text-white/60">
                        <div className="rounded-2xl bg-white/[0.04] p-4">
                            <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">Source</div>
                            <p className="mt-2">
                                {info.default_saved || Object.keys(info.profiles).some(isCustom)
                                    ? `Saved from the admin${info.updated_at ? ' on ' + new Date(info.updated_at).toLocaleString() : ''}, over the file defaults in config/recommend.php.`
                                    : 'Using the file defaults from config/recommend.php.'}
                            </p>
                            <button type="button" disabled={busy}
                                    onClick={() => window.confirm('Drop every saved value and profile, back to the file defaults?') && run(resetRecommendSettings('all'))}
                                    className="mt-3 rounded-xl bg-white/[0.08] px-3 py-2 text-[12px] font-semibold text-white/70 hover:text-white">
                                Back to file defaults
                            </button>
                        </div>
                        <div className="rounded-2xl bg-white/[0.04] p-4">
                            <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">How the score works</div>
                            <p className="mt-2">The sound match (all 84 fingerprint values together, corrected for crowded areas of the catalog) is the base. Genre and era bonuses then close a share of the remaining gap to 100%, so a strong match gains a little and an average one gains more. Locks are hard filters. Saved changes reach the app and the API within a minute.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
